// Runtime app config from the backend (GET /api/config).
//
// The SPA can't tell which deployment lane it's in on its own — NODE_ENV
// is 'production' in both stage and prod. The backend's APP_ENV is the
// single source of truth; we read it once on boot to decide whether to
// show the test-environment banner.
//
// The Stripe PUBLISHABLE key is also delivered here at RUNTIME (not baked
// as a VITE_ var) so one promoted image runs in every lane with its own
// per-lane key — see ProviderCardField.tsx. Non-price config (CLAUDE.md
// §Caching permits caching schema/enum/config that isn't pricing).

import { api } from './client.js';

export type AppEnvironment = 'local' | 'stage' | 'production';

export type AppConfig = {
  environment: AppEnvironment;
  // pk_test_… / pk_live_… for the active lane, or '' when Stripe isn't
  // configured (e.g. PAYMENT_PROVIDER=mock).
  stripe_publishable_key: string;
  // Release v1 pricing lockdown. When true, the portal shows no online price —
  // the price flow is replaced by a "get your price from our team" contact-sales
  // panel. The backend enforces this authoritatively; this flag only drives UX.
  // Optional so an older backend (pre-flag) parses as locked (fail-safe) below.
  pricing_locked?: boolean;
  // Analytics/APM keys served at runtime (ADR-014), NOT baked as VITE_* build
  // args — so one promoted image runs every lane with that lane's keys. Empty
  // string when the lane's env var is unset (always empty locally); the
  // analytics chokepoint treats an empty key as "off". Optional so an older
  // backend parses as unset/off. The AI value is a client-safe ingestion key;
  // the Clarity id is public.
  clarity_project_id?: string;
  app_insights_connection_string?: string;
};

export async function getConfig(): Promise<AppConfig> {
  const res = await api.get<AppConfig>('/config');
  return res.data;
}
